// v1.0.0 - 2026-01-27 - Branded wrapper for whitelabel theming

import { useEffect, type ReactNode } from 'react'
import type { CERSettings } from '@/lib/ti-calculator/types'

interface BrandedWrapperProps {
  settings: CERSettings | null
  children: ReactNode
}

/**
 * Wraps the app and applies CER-specific CSS variables for whitelabel theming.
 * CSS variables are applied to :root to override defaults in index.css.
 */
export function BrandedWrapper({ settings, children }: BrandedWrapperProps) {
  useEffect(() => {
    if (!settings) return

    const root = document.documentElement

    // Apply branding colors
    if (settings.branding.primary_color) {
      root.style.setProperty('--color-primary', settings.branding.primary_color)
      // Generate hover variant (slightly lighter)
      root.style.setProperty(
        '--color-primary-hover',
        adjustColor(settings.branding.primary_color, 15)
      )
      root.style.setProperty(
        '--color-primary-light',
        adjustColor(settings.branding.primary_color, 30)
      )
    }

    if (settings.branding.accent_color) {
      root.style.setProperty('--color-accent', settings.branding.accent_color)
      root.style.setProperty(
        '--color-accent-hover',
        adjustColor(settings.branding.accent_color, -10)
      )
      root.style.setProperty(
        '--color-accent-light',
        adjustColor(settings.branding.accent_color, 30)
      )
    }

    // Cleanup on unmount or settings change
    return () => {
      root.style.removeProperty('--color-primary')
      root.style.removeProperty('--color-primary-hover')
      root.style.removeProperty('--color-primary-light')
      root.style.removeProperty('--color-accent')
      root.style.removeProperty('--color-accent-hover')
      root.style.removeProperty('--color-accent-light')
    }
  }, [settings])

  return <>{children}</>
}

/**
 * Adjust color lightness.
 * Positive amount = lighter, negative = darker.
 */
function adjustColor(hex: string, amount: number): string {
  // Remove # if present
  hex = hex.replace(/^#/, '')

  // Parse RGB
  const num = parseInt(hex, 16)
  let r = (num >> 16) + amount
  let g = ((num >> 8) & 0x00ff) + amount
  let b = (num & 0x0000ff) + amount

  // Clamp values
  r = Math.max(0, Math.min(255, r))
  g = Math.max(0, Math.min(255, g))
  b = Math.max(0, Math.min(255, b))

  // Convert back to hex
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}

export default BrandedWrapper
